import { useHistory } from 'react-router-dom'
import { useDebounce } from 'react-use'
import { TimerIcon, Text, useTooltip, useModal } from 'vvs-uikit'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { useTranslation } from 'contexts/Localization'
import { useGameManager } from 'state/vvsgotchi/hooks/useGameManager'
import useGachapon, { useGachaponPopupShown } from 'state/vvsgotchi/hooks/useGachapon'
import useEndOfUTCDayCountdown from 'hooks/useEndOfUTCDayCountdown'
import { formatUrlSearch } from 'utils/urlSearch'
import { Search } from 'config/constants/paths'

import GachaponPopupModal from './Modal/PopupModal'
import { GachaponState } from './types'
import { GachaponButton, GachaponButtonIcon, GachaponCountdownLabel, GachaponDoneIcon } from './styles'

const GachaponBtn = () => {
  const { t } = useTranslation()
  const { owner, refresh } = useGotchiOwner()
  const history = useHistory()
  const gachapon = useGachapon()

  const [popupShown, setPopupShown] = useGachaponPopupShown()

  const { targetRef, tooltip, tooltipVisible } = useTooltip(t('Hey! A special gift is waiting for you!'), {
    placement: 'bottom-end',
    tooltipOffset: [0, 15],
    trigger: 'hover-no-touch',
  })

  const [openPopupModal] = useModal(<GachaponPopupModal history={history} />, true, true, 'vvsgotchiGachaponPopup')

  const gachaponActive =
    gachapon.loaded && gachapon.gachaponStart.getTime() < Date.now() && gachapon.gachaponEnd.getTime() > Date.now()
  const gachaponState: GachaponState = gachapon.hasRedeemGacha ? 'done' : gachapon.isGachaReady ? 'eligible' : 'init'

  useDebounce(
    () => {
      if (owner?.isOnboarded && gachaponActive && !popupShown && gachaponState !== 'done') {
        openPopupModal()
        setPopupShown(true)
      }
    },
    500,
    [owner, gachaponActive, popupShown, gachaponState],
  )

  if (!gachaponActive) return null

  return (
    <GachaponButton
      state={gachaponState}
      ref={targetRef}
      onTouchEnd={(e) => {
        e.stopPropagation()
        e.preventDefault()
        history.replace({
          search: formatUrlSearch(Search.VVS_GOTCHI_GACHAPON),
        })
      }}
      onClick={() => {
        history.replace({
          search: formatUrlSearch(Search.VVS_GOTCHI_GACHAPON),
        })
      }}
    >
      <GachaponButtonIcon
        state={gachaponState}
        src={gachaponState !== 'done' ? '/images/vvsGotchi/gachapon-icon.png' : '/images/vvsGotchi/gift-2.png'}
        alt="Gachapon Icon"
        width="34px"
      />
      <GachaponCountdownLabel state={gachaponState}>
        <TimerIcon width="14px" fill="black" pt="2px" />
        <GachaponCountDown refresh={refresh} />
      </GachaponCountdownLabel>
      {gachaponState === 'done' && <GachaponDoneIcon src="/images/vvsGotchi/check.png" />}
      {gachaponState !== 'done' && tooltipVisible && tooltip}
    </GachaponButton>
  )
}

const GachaponBtnIfAvailable = () => {
  const { isGameStarted } = useGameManager()

  if (!isGameStarted) return null
  return <GachaponBtn />
}

export default GachaponBtnIfAvailable

const GachaponCountDown: React.FC<{ refresh?: () => void }> = ({ refresh }) => {
  const { t } = useTranslation()
  const timeExcerptMap = useEndOfUTCDayCountdown(refresh)

  return (
    <Text color="black" fontSize="13px" fontWeight="700" pl="3px">
      {timeExcerptMap.h > 0
        ? t('%h%h left', timeExcerptMap)
        : timeExcerptMap.m > 0
        ? t('%m%m left', timeExcerptMap)
        : t('%s%s left', timeExcerptMap)}
    </Text>
  )
}
